/* eslint-disable array-callback-return */
export default class NodeCluster {
    // Public:
    constructor(cParams) {
        this.saveParams(cParams);
        this._masterNodesObjects = [];
        this.masterNodes = [];
        this.defineProperties();
    }

    add(node) {
        if (this.isNodeNew(node)) {
            this.saveNode(node);
            this.addMasterNode(node);
        }
    }

    compute() {
        this.classifySlaveNodes();
        this.listSlaveNodes();
        this.listAllNodes();
        this.listForcibleNodes();
        this.getNodesObjects();
        this.assignClusterToNodes();
        this.computeRadii();
    }

    setId(id) {
        this.id = id;
    }

    setNumberOfClusters(n) {
        this.nClusters = n;
    }

    setSiblings(siblings) {
        this.siblings = siblings;
    }

    hasThisMasterNumber(number) {
        const itHas = this.masterNodes.indexOf(number) !== -1;
        return itHas;
    }

    // Private:
    saveParams(cParams) {
        this._allNodesObject = cParams.nodes;
        this._masterRadiusParams = cParams.masterRadiusParams;
        this._clusterRadiusParams = cParams.clusterRadiusParams;
        this._soloRadiusParams = cParams.soloRadiusParams;
        this._sharedRadiusParams = cParams.sharedRadiusParams;
        this._minimumRadius = cParams.minimumRadius;
        this._nNodesToMergeOrbitals = cParams.nNodesToMergeOrbitals;
    }

    saveNode(node) {
        this._masterNodesObjects.push(node);
    }

    addMasterNode(node) {
        node.category = "master";
        let i = node.number;
        this.masterNodes.push(i);
        this.masterNodes.sort();
    }

    classifySlaveNodes() {
        this.computeMasterGroups();
        this.initNodeNumbers();
        const mastersNodes = this._masterNodesObjects;
        mastersNodes.forEach(masterNode => {
            const groupA = masterNode.group[0];
            this.addSoloNodes(masterNode);
            let allSharedNodes = masterNode.sharedNumbers;
            allSharedNodes.forEach((sharedNodes, groupB) => {
                const itIsNotItself = groupA !== groupB;
                if (itIsNotItself) {
                    sharedNodes.forEach(number => {
                        const node = this.findNodeByNumber(number);
                        if (this.isNodeInCluster(node)) {
                            this.assignNodeAsCluster(number);
                        } else {
                            this.assignNodeAsShared(number);
                        }
                    });
                }
            });
        });
    }

    addSoloNodes(master) {
        let newSolos = master.soloNumbers;
        for (let i = 0; i < newSolos.length; i++) {
            const soloIndex = newSolos[i];
            if (this.soloNumbers.indexOf(soloIndex) === -1) {
                this.soloNumbers.push(soloIndex);
                let slave = this._allNodesObject[soloIndex];
                slave.category = "solo";
            }
        }
    }

    isNodeInCluster(node) {
        let itIsInCluster = true;
        const clusterMastersNumbers = this.masterNodes;
        const ownersNumbers = node.ownersNumbers;
        ownersNumbers.forEach(number => {
            const itIsNotListed = clusterMastersNumbers.indexOf(number) === -1;
            if (itIsNotListed) itIsInCluster = false;
        });
        return itIsInCluster;
    }

    assignNodeAsCluster(number) {
        const itIsNotAlreadyIncluded = this.clusterNumbers.indexOf(number) === -1;
        if (itIsNotAlreadyIncluded) {
            this.clusterNumbers.push(number);
            let slaveNode = this.findNodeByNumber(number);
            slaveNode.category = "cluster";
        }
    }

    assignNodeAsShared(number) {
        const itIsNotAlreadyIncluded = this.sharedNumbers.indexOf(number) === -1;
        if (itIsNotAlreadyIncluded) {
            this.sharedNumbers.push(number);
            let slaveNode = this.findNodeByNumber(number);
            slaveNode.category = "shared";
        }
    }

    isNodeNew(node) {
        let i = node.number;
        let itIs = this.masterNodes.indexOf(i) === -1;
        return itIs;
    }

    listSlaveNodes() {
        let indices = [];
        indices.selfConcat(this.soloNumbers);
        indices.selfConcat(this.clusterNumbers);
        indices.selfConcat(this.sharedNumbers);
        this.slaveNumbers = indices;
    }

    listAllNodes() {
        let indices = [];
        indices.selfConcat(this.masterNodes);
        indices.selfConcat(this.slaveNumbers);
        this.nodesIndices = indices;
    }

    listForcibleNodes() {
        let indices = [];
        indices.selfConcat(this.masterNodes);
        indices.selfConcat(this.soloNumbers);
        indices.selfConcat(this.clusterNumbers);
        this.forcibleNodesIndices = indices;
    }

    getNodesObjects() {
        let nodesObjects = [];
        let allNodes = this._allNodesObject;
        let indices = this.nodesIndices;
        for (let i = 0; i < indices.length; i++) {
            const index = indices[i];
            const node = allNodes[index];
            nodesObjects.push(node);
        }
        this.nodesObjects = nodesObjects;
    }

    assignClusterToNodes() {
        let nodes = this.nodesObjects;
        nodes.forEach(node => {
            node.clusters.push(this);
        });
    }

    initNodeNumbers() {
        this.soloNumbers = [];
        this.clusterNumbers = [];
        this.sharedNumbers = [];
    }

    findNodeByNumber(targetNumber) {
        let targetNode = undefined;
        const nodes = this._allNodesObject;
        nodes.forEach(node => {
            const number = node.number;
            const itIsTargetNumber = targetNumber === number;
            if (itIsTargetNumber) targetNode = node;
        });
        return targetNode;
    }

    computeRadii() {
        this.computeMasterRadius();
        this.computeSlaveRadii();
        this.computeMaximumRadius();
    }

    computeMasterRadius() {
        var R = 0;
        const masterNumbers = this.masterNodes;
        const nMasters = masterNumbers.length;
        switch (nMasters) {
            case 1:
                break;
            case 2:
                let perimeter = this.computeEnrichedPerimeter(masterNumbers);
                R = perimeter / 2;
                break;
            default:
                const params = this._masterRadiusParams;
                R = this.computeResultingOrbitalRadius(masterNumbers, 0, params);
                break;
        }
        this.masterRadius = R;
    }

    computeSlaveRadii() {
        const threshold = this._nNodesToMergeOrbitals;
        const nClusters = this.clusterNumbers.length;
        const nSolos = this.soloNumbers.length;
        const nShared = this.sharedNumbers.length;
        const nTotal = nClusters + nSolos + nShared;
        if (nTotal < threshold) this.computeRadiusForAll();
        else if (nSolos + nShared < threshold) this.computeRadiusMergingSoloAndShared();
        else if (nClusters + nSolos < threshold) this.computeRadiusMergingClusterAndSolo();
        else this.computeRadiiSeparately();
    }

    computeRadiusForAll() {
        this.computeClusterRadius();
        const params = this._soloRadiusParams;
        const R0 = this.clusterRadius;
        const numbers = this.slaveNumbers;
        const Rc = this.computeResultingOrbitalRadius(numbers, R0, params);
        const Rmin = this._minimumRadius;
        const R = Math.max(Rc, Rmin);
        this.clusterRadius = R;
        this.soloRadius = R;
        this.sharedRadius = R;
    }

    computeRadiusMergingClusterAndSolo() {
        this.computeClusterRadius();
        const params = this._soloRadiusParams;
        const R0 = this.clusterRadius;
        let numbers = this.clusterNumbers;
        numbers.selfConcat(this.soloNumbers);
        const Rc = this.computeResultingOrbitalRadius(numbers, R0, params);
        const Rmin = this._minimumRadius;
        const R = Math.max(Rc, Rmin);
        this.clusterRadius = R;
        this.soloRadius = R;
        this.computeSharedRadius();
    }

    computeRadiusMergingSoloAndShared() {
        this.computeClusterRadius();
        const params = this._soloRadiusParams;
        const R0 = this.clusterRadius;
        let numbers = this.soloNumbers;
        numbers.selfConcat(this.sharedNumbers);
        const Rc = this.computeResultingOrbitalRadius(numbers, R0, params);
        const Rmin = this._minimumRadius;
        const R = Math.max(Rc, Rmin);
        this.soloRadius = R;
        this.sharedRadius = R;
    }

    computeRadiiSeparately() {
        this.computeClusterRadius();
        this.computeSoloRadius();
        this.computeSharedRadius();
    }

    computeClusterRadius() {
        const params = this._clusterRadiusParams;
        const R0 = this.masterRadius;
        const numbers = this.clusterNumbers;
        const Rc = this.computeResultingOrbitalRadius(numbers, R0, params);
        const Rmin = this._minimumRadius;
        this.clusterRadius = Math.max(Rc, Rmin);
    }

    computeSoloRadius() {
        const params = this._soloRadiusParams;
        const R0 = this.clusterRadius;
        const numbers = this.soloNumbers;
        this.soloRadius = this.computeResultingOrbitalRadius(numbers, R0, params);
    }

    computeSharedRadius() {
        const params = this._sharedRadiusParams;
        const R0 = this.soloRadius;
        const numbers = this.sharedNumbers;
        this.sharedRadius = this.computeResultingOrbitalRadius(numbers, R0, params);
    }

    computeMaximumRadius() {
        let R = Math.max(this.soloRadius, this.clusterRadius, this.sharedRadius);
        this.radius = R;
    }

    computeThisRadius(nodes) {
        let perimeter = this.computeEnrichedPerimeter(nodes);
        let R = perimeter / (2 * Math.PI);
        return R;
    }

    computeEnrichedPerimeter(indices) {
        let p = 0;
        let Rmin = 50;
        this._allNodesObject.find(node => {
            const itIsNodeInCluster = indices.indexOf(node.number) !== -1;
            if (itIsNodeInCluster) {
                const Rnode = node.radius;
                const R = Math.max(Rnode, Rmin);
                p = p + 2 * R;
            }
        });
        return p;
    }

    computeResultingOrbitalRadius(nodes, R0, params) {
        const k0 = params.k0;
        const n = params.n;
        const Rmin = R0 + k0 * Math.pow(R0, n);
        const kFit = params.kFit;
        const Rfit = this.computeThisRadius(nodes);
        const Rc = kFit * Rfit;
        const R = Math.max(Rc, Rmin);
        return R;
    }

    computeMasterGroups() {
        let masterNodes = this._masterNodesObjects;
        let masterGroups = [];
        for (let i = 0; i < masterNodes.length; i++) {
            const node = masterNodes[i];
            const group = node.group[0];
            masterGroups.push(group);
        }
        this.masterGroups = masterGroups;
    }

    defineProperties() {
        this.defineX();
        this.defineY();
    }

    defineX() {
        let getFunc = () => this.computeX();
        let setFunc = x => this.setCoordinateX(x);
        Object.defineProperty(this, "x", {get: getFunc, set: setFunc});
    }

    defineY() {
        let getFunc = () => this.computeY();
        let setFunc = y => this.setCoordinateY(y);
        Object.defineProperty(this, "y", {get: getFunc, set: setFunc});
    }

    computeX() {
        let masterNodes = this._masterNodesObjects;
        let x = this.computeAverage(masterNodes, "x");
        return x;
    }

    computeY() {
        let masterNodes = this._masterNodesObjects;
        let y = this.computeAverage(masterNodes, "y");
        return y;
    }

    computeAverage(array, prop) {
        let N = array.length;
        let accum = 0;
        for (let i = 0; i < N; i++) {
            const element = array[i];
            accum += element[prop];
        }
        let avg = accum / N;
        return avg;
    }

    setCoordinateX(x) {
        let x0 = this.x;
        let dx = x - x0;
        this.translateX(dx);
    }

    setCoordinateY(y) {
        let y0 = this.y;
        let dy = y - y0;
        this.translateY(dy);
    }

    translateX(dx) {
        let nodes = this.nodesObjects;
        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            let nClusters = node.clusters.length;
            node.x += dx / nClusters;
        }
    }

    translateY(dy) {
        let nodes = this.nodesObjects;
        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            let nClusters = node.clusters.length;
            node.y += dy / nClusters;
        }
    }

    isSingleNoded() {
        let masterNodes = this.masterNodes;
        if (masterNodes.length === 1) return true;
        else return false;
    }
}
